import { useRef } from "react";
import { ImageResolution } from "./useImageResizer";

export interface UploadTask {
  id: string;
  resolutions: Record<
    ImageResolution,
    {
      file: File;
      presigned: { uploadUrl: string };
    }
  >;
  onProgress?: (p: number) => void;
  onSuccess?: () => void;
  onError?: (err: any) => void;
}

export function useUploadQueueWithProgress(maxConcurrent = 3) {
  const queue = useRef<UploadTask[]>([]);
  const activeCount = useRef(0);

  const uploadBlobWithProgress = (
    blob: Blob,
    url: string,
    onProgress: (p: number) => void
  ): Promise<void> => {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open("PUT", url);
      xhr.setRequestHeader("Content-Type", blob.type);

      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) {
          const percent = Math.round((e.loaded / e.total) * 100);
          onProgress(percent);
        }
      };

      xhr.onload = () => resolve();
      xhr.onerror = () => reject();
      xhr.send(blob);
    });
  };

  const runTask = async (task: UploadTask) => {
    const { resolutions, onProgress, onSuccess, onError } = task;
    try {
      const progressMap: Partial<Record<ImageResolution, number>> = {};

      const updateProgress = () => {
        const values = Object.values(progressMap);
        const percent =
          values.length === 0
            ? 0
            : Math.round(values.reduce((a, b) => a + b, 0) / values.length);
        onProgress?.(percent);
      };

      await Promise.all(
        Object.entries(resolutions).map(([res, value]) => {
          return uploadBlobWithProgress(
            value.file,
            value.presigned.uploadUrl,
            (p) => {
              progressMap[res as ImageResolution] = p;
              updateProgress();
            }
          );
        })
      );

      onProgress?.(100);
      onSuccess?.();
    } catch (err) {
      onProgress?.(-1);
      onError?.(err);
    }
  };

  const runNext = () => {
    if (activeCount.current >= maxConcurrent || queue.current.length === 0)
      return;

    const task = queue.current.shift();
    if (!task) return;

    activeCount.current++;
    runTask(task).finally(() => {
      activeCount.current--;
      runNext();
    });

    runNext();
  };

  const enqueue = (task: UploadTask) => {
    queue.current.push(task);
    runNext();
  };

  return {
    enqueue
  };
}
